import {
  type CreateVisaPricingTierRequest,
  type CreateVisaRequest,
  type UpdateVisaPricingTierRequest,
  type UpdateVisaRequest,
  type VisaApprovalRequest,
  type VisaDetail,
  VisaDetailSchema,
  type VisaListQuery,
  type VisaListResponse,
  VisaListResponseSchema,
  type VisaPricingTierOut,
  VisaPricingTierOutSchema,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

export const listVisas = async (query: VisaListQuery): Promise<VisaListResponse> => {
  const { data } = await apiClient.get<unknown>('/visas', { params: query });
  return VisaListResponseSchema.parse(data);
};

export const getVisa = async (id: string): Promise<VisaDetail> => {
  const { data } = await apiClient.get<unknown>(`/visas/${id}`);
  return VisaDetailSchema.parse(data);
};

export const createVisa = async (payload: CreateVisaRequest): Promise<VisaDetail> => {
  const { data } = await apiClient.post<unknown>('/visas', payload);
  return VisaDetailSchema.parse(data);
};

export const updateVisa = async (id: string, payload: UpdateVisaRequest): Promise<VisaDetail> => {
  const { data } = await apiClient.patch<unknown>(`/visas/${id}`, payload);
  return VisaDetailSchema.parse(data);
};

export const approveVisa = async (
  id: string,
  payload: VisaApprovalRequest,
): Promise<VisaDetail> => {
  const { data } = await apiClient.post<unknown>(`/visas/${id}/approval`, payload);
  return VisaDetailSchema.parse(data);
};

export const deleteVisa = async (id: string): Promise<void> => {
  await apiClient.delete(`/visas/${id}`);
};

export const bulkDeleteVisas = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/visas/bulk-delete', { ids });
  return data;
};

// ---------- Pricing tier sub-resource ----------

const PricingTierListSchema = z.array(VisaPricingTierOutSchema);

export const listVisaPricingTiers = async (visaId: string): Promise<VisaPricingTierOut[]> => {
  const { data } = await apiClient.get<unknown>(`/visas/${visaId}/pricing-tiers`);
  return PricingTierListSchema.parse(data);
};

export const createVisaPricingTier = async (
  visaId: string,
  payload: CreateVisaPricingTierRequest,
): Promise<VisaPricingTierOut> => {
  const { data } = await apiClient.post<unknown>(`/visas/${visaId}/pricing-tiers`, payload);
  return VisaPricingTierOutSchema.parse(data);
};

export const updateVisaPricingTier = async (
  visaId: string,
  tierId: string,
  payload: UpdateVisaPricingTierRequest,
): Promise<VisaPricingTierOut> => {
  const { data } = await apiClient.patch<unknown>(
    `/visas/${visaId}/pricing-tiers/${tierId}`,
    payload,
  );
  return VisaPricingTierOutSchema.parse(data);
};

export const deleteVisaPricingTier = async (visaId: string, tierId: string): Promise<void> => {
  await apiClient.delete(`/visas/${visaId}/pricing-tiers/${tierId}`);
};
